import { useContext, useRef } from 'react';
import { Table as TableAntd, Tag } from 'antd';
import { SortOrder } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import TableCard from '../../../../../components/TableCard';
import TableActionButton from '../../../../../components/TableActionButton';
import { useGetColumnSearchPropsData } from '../../../../../hooks/FilterTable/date';
import { useGetColumnSearchProps } from '../../../../../hooks/FilterTable/stringClearFiltros';
import { IVoucher } from '../../../../../interfaces';
import { formatDateStringToDateAndTime } from "../../../../../utils/Conversores";
import { ThemeContext } from '../../../../../contexts/ThemeContext';

interface ITableData extends IVoucher {
    key: number | undefined;
    reward_name: string;
}

interface IProps {
    vouchers: IVoucher[] | undefined;
}

export default function TabVouchers({
    vouchers,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const refFuncaoLimparFiltrosString = useRef<any>(null);
    const navigate = useNavigate();

    const columns = [
        {
            title: 'Recompensa',
            dataIndex: 'reward_name',
            key: 'reward_name',
            sorter: (a: ITableData, b: ITableData) => a?.reward?.name.localeCompare(b?.reward?.name || '') || 0,
            showSorterTooltip: false,
            ...useGetColumnSearchProps({ dataIndex: 'reward_name', ref: refFuncaoLimparFiltrosString }),
            width: '28%',
            render: (name: string) => <span style={{ fontWeight: 600 }}>{name || '--'}</span>,
        },
        {
            title: 'Código',
            dataIndex: 'redemption_code',
            key: 'redemption_code',
            ...useGetColumnSearchProps({ dataIndex: 'redemption_code', ref: refFuncaoLimparFiltrosString }),
            render: (code: string) => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1677ff' }}>{code}</span>,
        },
        {
            title: 'Pontos Usados',
            dataIndex: 'points_used',
            key: 'points_used',
            render: (record: number) => <span style={{ fontWeight: 600 }}>{record ? record.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0}</span>,
            sorter: (a: ITableData, b: ITableData) => (a?.points_used || 0) - (b?.points_used || 0),
            showSorterTooltip: false,
            align: 'right' as const,
        },
        {
            title: 'Data de Resgate',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: (a: ITableData, b: ITableData) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({ dataIndex: 'created_at' }),
            render: (data: any) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data) : '--'}
                </span>
            ),
            defaultSortOrder: 'descend' as keyof SortOrder,
        },
        {
            title: 'Expira',
            dataIndex: 'validity',
            key: 'validity',
            sorter: (a: ITableData, b: ITableData) => new Date(a.validity).getTime() - new Date(b.validity).getTime(),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({ dataIndex: 'validity' }),
            render: (data: any) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data) : '--'}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a: ITableData, b: ITableData) => a.status.localeCompare(b.status),
            filters: [
                { text: 'Emitido', value: 'Emitido' },
                { text: 'Resgatado', value: 'Resgatado' },
                { text: 'Expirado', value: 'Expirado' },
            ],
            onFilter: (value: any, record: IVoucher) => record.status === value,
            render: (_: any, request: IVoucher) => (
                <Tag
                    key={request.id}
                    color={request.status === "Resgatado" ? "success" : request.status === "Expirado" ? "error" : "default"}
                    style={{ borderRadius: 6, fontWeight: 600 }}
                >
                    {request.status}
                </Tag>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: '6%',
            render: (_: any, record: any) => (
                <TableActionButton
                    action="view"
                    tooltip="Visualizar voucher"
                    onClick={() => navigate(`/vouchers/${record.redemption_code}`)}
                />
            ),
        },
    ];

    if (!vouchers) {
        return null;
    }

    return (
        <div style={{ paddingTop: 8 }}>
            <TableCard>
                <TableAntd
                    className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                    columns={columns}
                    dataSource={vouchers.map((voucher) => ({
                        ...voucher,
                        key: voucher.id,
                        reward_name: voucher?.reward?.name || '',
                    }))}
                    onRow={(record) => ({
                        onClick: () => {
                            if (record.redemption_code) {
                                navigate(`/vouchers/${record.redemption_code}`);
                            }
                        },
                        style: {
                            cursor: record.redemption_code ? 'pointer' : 'default',
                        },
                    })}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                    }}
                />
            </TableCard>
        </div>
    );
}
